import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Image, Link2, FileCode, Settings, ChevronDown, ChevronUp,
  CheckCircle2, AlertTriangle, XCircle, Type, Hash, FileText,
  Shield, Eye, AtSign, Share2, Code2, Target, Zap, Lock, Bot, BookOpen,
  FileSearch, Gauge, Activity, Server, Smartphone, Monitor,
  Timer, HardDrive, Layers, Network, ArrowRightLeft, AlertOctagon,
  Languages, Cpu, Mail, ExternalLink, Wifi, Clock, CircuitBoard,
  Facebook, Twitter, Linkedin, Instagram, Youtube,
  Unplug, ShieldCheck, BarChart3, Paintbrush, BrainCircuit,
  RadioTower, Package, Blocks, SearchX,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const truncate = (str, n = 45) =>
  str && str.length > n ? str.slice(0, n) + '…' : str || '';

const CheckDot = ({ status }) => {
  if (status === 'pass') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />;
  if (status === 'warn') return <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />;
  return <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />;
};

const checkBadgeColor = (status) => {
  if (status === 'pass') return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800';
  if (status === 'warn') return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800';
  return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
};

const presenceBadge = (present, label, icon) => (
  <div key={label} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${present ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'}`}>
    {icon}
    {label}
  </div>
);

/* ─── 1. PageDataPanel ───────────────────────────────────────────────────── */
export const PageDataPanel = ({ pageData, auditScores }) => {
  if (!pageData) return null;

  const {
    url, title, meta_description, h1 = [], h2 = [], h3 = [],
    word_count = 0, images_detailed = [], internal_links = [],
    external_links = [], schema_markup = [], open_graph = {},
    twitter_card = {}, canonical, has_viewport_meta, is_https,
    meta_robots, keyword_density, keyword_occurrences,
  } = pageData;

  const totalImages = images_detailed.length;
  const missingAlt = images_detailed.filter(i => !i.alt).length;
  const lazyLoaded = images_detailed.filter(i => i.loading === 'lazy').length;
  const modernFormat = images_detailed.filter(i => ['webp', 'avif'].includes(i.format)).length;

  const wordStatus = word_count < 300 ? 'red' : word_count < 800 ? 'amber' : 'emerald';
  const wordBarPct = Math.min((word_count / 2000) * 100, 100);
  const wordBarColor = { red: 'bg-red-500', amber: 'bg-amber-500', emerald: 'bg-emerald-500' }[wordStatus];

  const totalLinks = internal_links.length + external_links.length;
  const intPct = totalLinks > 0 ? Math.round((internal_links.length / totalLinks) * 100) : 0;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Globe className="w-5 h-5 text-indigo-600" />
          Page Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* SERP Snippet Preview */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">SERP Preview</p>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-0.5">
            <p className="text-sm text-emerald-700 dark:text-emerald-500 truncate">{url}</p>
            <h3 className={`text-[17px] font-medium leading-snug cursor-default hover:underline ${title ? 'text-blue-700 dark:text-blue-400' : 'text-red-500 italic'}`}>
              {title || 'No title tag set'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 leading-snug mt-0.5">
              {meta_description || 'No meta description set — Google will auto-generate a snippet, often from poor-quality body text.'}
            </p>
          </div>
          <div className="flex gap-3 mt-2 text-xs text-slate-500">
            <span className={title ? 'text-emerald-600' : 'text-red-500'}>
              Title: {title ? `${title.length} chars` : 'MISSING'}
            </span>
            <span className={meta_description ? 'text-emerald-600' : 'text-red-500'}>
              Desc: {meta_description ? `${meta_description.length} chars` : 'MISSING'}
            </span>
          </div>
        </div>

        {/* Metrics grid */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Page Metrics</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Word Count */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-slate-500">
                <FileText className="w-3.5 h-3.5" />
                <span className="text-xs">Word Count</span>
              </div>
              <p className={`text-xl font-bold font-heading text-${wordStatus}-600`}>{word_count.toLocaleString()}</p>
              <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${wordBarColor}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${wordBarPct}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              </div>
            </div>

            {/* Headings */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-slate-500">
                <Type className="w-3.5 h-3.5" />
                <span className="text-xs">Headings</span>
              </div>
              <p className="text-xl font-bold font-heading">{h1.length + h2.length + h3.length}</p>
              <div className="flex gap-1 text-xs">
                <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">H1:{h1.length}</span>
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">H2:{h2.length}</span>
                <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded">H3:{h3.length}</span>
              </div>
            </div>

            {/* Images */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-slate-500">
                <Image className="w-3.5 h-3.5" />
                <span className="text-xs">Images</span>
              </div>
              <p className="text-xl font-bold font-heading">{totalImages}</p>
              <div className="text-xs text-slate-500">
                {missingAlt > 0 ? (
                  <span className="text-red-500">{missingAlt} no alt</span>
                ) : (
                  <span className="text-emerald-600">All have alt</span>
                )}
              </div>
            </div>

            {/* Links */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-slate-500">
                <Link2 className="w-3.5 h-3.5" />
                <span className="text-xs">Links</span>
              </div>
              <p className="text-xl font-bold font-heading">{totalLinks}</p>
              {totalLinks > 0 && (
                <div className="h-1.5 bg-blue-200 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${intPct}%` }} />
                </div>
              )}
              <p className="text-xs text-slate-500">{internal_links.length} int / {external_links.length} ext</p>
            </div>
          </div>
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Keyword Density</p>
            <p className={`text-lg font-bold font-heading ${keyword_density > 0 ? (keyword_density > 4 ? 'text-red-500' : 'text-emerald-600') : 'text-slate-400'}`}>
              {keyword_density != null ? `${keyword_density}%` : 'N/A'}
            </p>
            <div className="text-xs text-slate-500 mt-1 flex flex-col gap-0.5">
              {keyword_occurrences > 0 && <span>{keyword_occurrences}× found</span>}
              {pageData.keyword_in_first_100_words && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> In first 100 words</span>}
              {pageData.keyword_in_first_100_words === false && <span className="text-amber-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Not in first 100 words</span>}
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Schema Markup</p>
            <p className={`text-lg font-bold font-heading ${schema_markup.length > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {schema_markup.length}
            </p>
            <p className="text-xs text-slate-500">{schema_markup.length > 0 ? 'JSON-LD blocks' : 'None found'}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Business Type</p>
            <p className="text-lg font-bold font-heading text-indigo-600 capitalize">
              {pageData.business_type ? pageData.business_type.replace('_', ' ') : 'General'}
            </p>
            <p className="text-xs text-slate-500">AI detected</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Lazy Images</p>
            <p className={`text-lg font-bold font-heading ${lazyLoaded === totalImages && totalImages > 0 ? 'text-emerald-600' : 'text-amber-500'}`}>
              {lazyLoaded}/{totalImages}
            </p>
            <p className="text-xs text-slate-500">lazy-loaded</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Modern Formats</p>
            <p className={`text-lg font-bold font-heading ${modernFormat > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
              {modernFormat}/{totalImages}
            </p>
            <p className="text-xs text-slate-500">WebP/AVIF</p>
          </div>
        </div>

        {/* Heading Hierarchy Tree */}
        {(h1.length > 0 || h2.length > 0 || h3.length > 0) && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Heading Hierarchy</p>
            <div className="space-y-1 font-mono text-sm">
              {h1.slice(0, 2).map((text, i) => (
                <div key={`h1-${i}`} className="flex items-start gap-2">
                  <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 text-xs rounded font-sans font-bold flex-shrink-0">H1</span>
                  <span className="text-slate-700 dark:text-slate-300 truncate text-xs leading-5">{truncate(text, 60)}</span>
                </div>
              ))}
              {h2.slice(0, 3).map((text, i) => (
                <div key={`h2-${i}`} className="flex items-start gap-2 pl-4 border-l-2 border-blue-200 dark:border-blue-800">
                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs rounded font-sans font-bold flex-shrink-0">H2</span>
                  <span className="text-slate-600 dark:text-slate-400 truncate text-xs leading-5">{truncate(text, 55)}</span>
                </div>
              ))}
              {h2.length > 3 && <p className="pl-4 text-xs text-slate-400 border-l-2 border-blue-200">+{h2.length - 3} more H2 tags</p>}
              {h3.slice(0, 2).map((text, i) => (
                <div key={`h3-${i}`} className="flex items-start gap-2 pl-8 border-l-2 border-cyan-200 dark:border-cyan-800">
                  <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 text-xs rounded font-sans font-bold flex-shrink-0">H3</span>
                  <span className="text-slate-500 dark:text-slate-500 truncate text-xs leading-5">{truncate(text, 50)}</span>
                </div>
              ))}
              {h3.length > 2 && <p className="pl-8 text-xs text-slate-400 border-l-2 border-cyan-200">+{h3.length - 2} more H3 tags</p>}
            </div>
          </div>
        )}

        {/* Presence Badges */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Technical Presence</p>
          <div className="flex flex-wrap gap-2">
            {pageData.status_code === 200 
              ? presenceBadge(true, `HTTP ${pageData.status_code}`, <Globe className="w-3 h-3" />)
              : presenceBadge(false, `HTTP ${pageData.status_code}`, <AlertTriangle className="w-3 h-3" />)}
            {presenceBadge(is_https, 'HTTPS', <Shield className="w-3 h-3" />)}
            {presenceBadge(has_viewport_meta, 'Viewport', <Eye className="w-3 h-3" />)}
            {presenceBadge(!!canonical, 'Canonical', <AtSign className="w-3 h-3" />)}
            {presenceBadge(Object.keys(open_graph || {}).length > 0, 'Open Graph', <Share2 className="w-3 h-3" />)}
            {presenceBadge(Object.keys(twitter_card || {}).length > 0, 'Twitter Card', <AtSign className="w-3 h-3" />)}
            {presenceBadge((pageData.social_links || []).length > 0, `Social Links: ${(pageData.social_links || []).length}`, <Share2 className="w-3 h-3" />)}
            {presenceBadge(schema_markup.length > 0, 'Schema', <Code2 className="w-3 h-3" />)}
            {presenceBadge(!!meta_robots, 'Meta Robots', <Settings className="w-3 h-3" />)}
            {presenceBadge(!!pageData.favicon_url, 'Favicon', <Globe className="w-3 h-3" />)}
            {presenceBadge(!!pageData.html_lang, `Lang: ${pageData.html_lang || 'Missing'}`, <Languages className="w-3 h-3" />)}
            {presenceBadge((pageData.hreflang_tags || []).length > 0, 'Hreflang', <Languages className="w-3 h-3" />)}
            {presenceBadge(pageData.is_compressed, 'Gzip/Brotli', <Wifi className="w-3 h-3" />)}
            {presenceBadge(!(pageData.mixed_content || []).length, 'No Mixed Content', <Lock className="w-3 h-3" />)}
          </div>
        </div>

      </CardContent>
    </Card>
  );
};

/* ─── 2. Enhanced SectionScoreBar ────────────────────────────────────────── */
const barColor = (pct) => {
  if (pct >= 70) return 'bg-emerald-500';
  if (pct >= 40) return 'bg-amber-500';
  return 'bg-red-500';
};

export const SectionScoreBar = ({ label, score = 0, max = 10, weight, checks = {}, issues = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const pct = Math.min((score / max) * 100, 100);
  const hasChecks = Object.keys(checks).length > 0;
  const hasIssues = issues.length > 0;

  return (
    <div className="space-y-1">
      <button
        className={`w-full text-left ${hasChecks ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={() => hasChecks && setExpanded((v) => !v)}
        disabled={!hasChecks}
      >
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400 font-medium">{label}</span>
          <div className="flex items-center gap-2 ml-2">
            {weight != null && (
              <span className="text-xs text-slate-400">{Math.round(weight * 100)}%</span>
            )}
            <span className="font-semibold tabular-nums">
              <span className={pct >= 70 ? 'text-emerald-600' : pct >= 40 ? 'text-amber-600' : 'text-red-600'}>{score}</span>
              <span className="text-slate-400">/{max}</span>
            </span>
            {hasChecks && (
              <span className="text-slate-400">
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            )}
          </div>
        </div>
        <div className="h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden mt-1">
          <motion.div
            className={`h-full rounded-full ${barColor(pct)}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
        </div>
      </button>

      {/* Expandable checks */}
      <AnimatePresence>
        {expanded && hasChecks && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-2 pb-1 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(checks).map(([criterion, status]) => (
                  <motion.span
                    key={criterion}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border font-medium ${checkBadgeColor(status)}`}
                  >
                    <CheckDot status={status} />
                    {criterion.replace(/_/g, ' ')}
                  </motion.span>
                ))}
              </div>

              {hasIssues && (
                <ul className="space-y-1">
                  {issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${issue.severity === 'high' ? 'text-red-500' : issue.severity === 'medium' ? 'text-amber-500' : 'text-blue-500'}`} />
                      <span>{issue.criterion}: {issue.notes}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── 3. SectionScoresCard ───────────────────────────────────────────────── */
export const SectionScoresCard = ({ sections = {} }) => {
  if (!sections || Object.keys(sections).length === 0) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Target className="w-5 h-5 text-indigo-600" />
          Section Breakdown
          <span className="text-xs font-normal text-slate-400 ml-1">(click to expand checks)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {Object.entries(sections).map(([key, val]) => (
            <SectionScoreBar
              key={key}
              label={val?.label || key.replace(/_/g, ' ')}
              score={val?.score ?? 0}
              max={val?.max ?? 10}
              weight={val?.weight}
              checks={val?.checks ?? {}}
              issues={val?.issues ?? []}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 4. Core Web Vitals Panel ───────────────────────────────────────────── */
const CWV_THRESHOLDS = {
  lcp: { good: 2500, poor: 4000, unit: 'ms', label: 'LCP' },
  cls: { good: 0.1, poor: 0.25, unit: '', label: 'CLS' },
  tbt: { good: 200, poor: 600, unit: 'ms', label: 'TBT' },
  fcp: { good: 1800, poor: 3000, unit: 'ms', label: 'FCP' },
};

const cwvColor = (value, thresholds) => {
  if (value == null) return 'text-slate-400';
  if (value <= thresholds.good) return 'text-emerald-600';
  if (value <= thresholds.poor) return 'text-amber-500';
  return 'text-red-500';
};

const cwvBg = (value, thresholds) => {
  if (value == null) return 'bg-slate-100 dark:bg-slate-800';
  if (value <= thresholds.good) return 'bg-emerald-50 dark:bg-emerald-900/20';
  if (value <= thresholds.poor) return 'bg-amber-50 dark:bg-amber-900/20';
  return 'bg-red-50 dark:bg-red-900/20';
};

export const CoreWebVitalsPanel = ({ cwv }) => {
  if (!cwv || !cwv.available) return null;

  const perfScore = cwv.performance_score;
  const perfColor = perfScore >= 90 ? 'text-emerald-600' : perfScore >= 50 ? 'text-amber-500' : 'text-red-500';
  const perfBg = perfScore >= 90 ? 'bg-emerald-500' : perfScore >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Core Web Vitals
          <Badge variant="outline" className="ml-auto text-xs">Mobile</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {perfScore != null && (
          <div className="flex items-center gap-4 mb-2">
            <div className={`text-3xl font-bold font-heading ${perfColor}`}>{perfScore}</div>
            <div className="flex-1">
              <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${perfBg}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${perfScore}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Lighthouse Performance Score</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(CWV_THRESHOLDS).map(([key, cfg]) => {
            const metric = cwv[key];
            const value = metric?.value;
            const display = metric?.display || (value != null ? `${Math.round(value)}${cfg.unit}` : 'N/A');
            return (
              <div key={key} className={`rounded-xl p-3 space-y-1 ${cwvBg(value, cfg)}`}>
                <p className="text-xs text-slate-500 font-medium">{cfg.label}</p>
                <p className={`text-xl font-bold font-heading ${cwvColor(value, cfg)}`}>{display}</p>
                <div className="flex gap-1">
                  <span className="text-[10px] text-emerald-600">≤{cfg.good}{cfg.unit}</span>
                  <span className="text-[10px] text-red-500">&gt;{cfg.poor}{cfg.unit}</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 5. Security Headers Panel ──────────────────────────────────────────── */
export const SecurityHeadersPanel = ({ securityHeaders }) => {
  if (!securityHeaders) return null;

  const headers = [
    { key: 'strict_transport_security', label: 'HSTS' },
    { key: 'content_security_policy', label: 'CSP' },
    { key: 'x_frame_options', label: 'X-Frame-Options' },
    { key: 'x_content_type_options', label: 'X-Content-Type' },
    { key: 'referrer_policy', label: 'Referrer Policy' },
    { key: 'permissions_policy', label: 'Permissions Policy' },
  ];

  const pct = securityHeaders.score_pct || 0;
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Lock className="w-5 h-5 text-violet-600" />
          Security Headers
          <span className="ml-auto text-sm font-bold">{securityHeaders.headers_present}/{securityHeaders.headers_total}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${barColor}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.7 }}
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {headers.map(({ key, label }) => (
            <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
              securityHeaders[key]
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400'
                : 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
            }`}>
              {securityHeaders[key] ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {label}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 6. Readability Panel ───────────────────────────────────────────────── */
export const ReadabilityPanel = ({ readability }) => {
  if (!readability) return null;

  const ease = readability.flesch_ease;
  const easeColor = ease >= 60 ? 'text-emerald-600' : ease >= 40 ? 'text-amber-500' : 'text-red-500';
  const easePct = Math.min(ease, 100);
  const easeBg = ease >= 60 ? 'bg-emerald-500' : ease >= 40 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-sky-600" />
          Readability
          <Badge variant="outline" className="ml-auto">{readability.level}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4">
          <div className={`text-3xl font-bold font-heading ${easeColor}`}>{ease}</div>
          <div className="flex-1">
            <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${easeBg}`}
                initial={{ width: 0 }}
                animate={{ width: `${easePct}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Flesch Reading Ease</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold font-heading">{readability.fk_grade}</p>
            <p className="text-xs text-slate-500">Grade Level</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold font-heading">{readability.avg_sentence_length}</p>
            <p className="text-xs text-slate-500">Avg Words/Sent</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold font-heading">{readability.total_sentences}</p>
            <p className="text-xs text-slate-500">Sentences</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 7. Robots & Sitemap Panel ──────────────────────────────────────────── */
export const RobotsSitemapPanel = ({ robotsTxt, sitemap }) => {
  if (!robotsTxt && !sitemap) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <FileSearch className="w-5 h-5 text-teal-600" />
          Robots & Sitemap
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* robots.txt */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">robots.txt</p>
          {robotsTxt?.exists ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <CheckCircle2 className="w-4 h-4" /> Found
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded">Disallow: {robotsTxt.disallow_rules}</span>
                <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded">Allow: {robotsTxt.allow_rules}</span>
                {robotsTxt.has_crawl_delay && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">Has Crawl-Delay</span>
                )}
                {robotsTxt.blocks_ai_crawlers && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded flex items-center gap-1">
                    <Bot className="w-3 h-3" /> Blocks AI Crawlers
                  </span>
                )}
                {!robotsTxt.blocks_ai_crawlers && (
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded flex items-center gap-1">
                    <Bot className="w-3 h-3" /> AI Crawlers OK
                  </span>
                )}
              </div>
              {robotsTxt.sitemaps_listed?.length > 0 && (
                <p className="text-xs text-slate-500">
                  Sitemaps listed: {robotsTxt.sitemaps_listed.length}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-red-500">
              <XCircle className="w-4 h-4" /> Not found
            </div>
          )}
        </div>

        {/* sitemap */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">XML Sitemap</p>
          {sitemap?.exists ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <CheckCircle2 className="w-4 h-4" /> Found
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded">URLs: {sitemap.url_count}</span>
                {sitemap.is_index && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Sitemap Index</span>
                )}
                {presenceBadge(sitemap.has_lastmod, 'lastmod', null)}
                {presenceBadge(sitemap.has_priority, 'priority', null)}
                {presenceBadge(sitemap.has_images, 'images', null)}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-red-500">
              <XCircle className="w-4 h-4" /> Not found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 8. AI Search Readiness Panel ───────────────────────────────────────── */
export const AIReadinessPanel = ({ aiReadiness }) => {
  if (!aiReadiness) return null;

  const score = aiReadiness.score || 0;
  const level = aiReadiness.level || 'Unknown';
  const checks = aiReadiness.checks || {};

  const scoreColor = score >= 75 ? 'text-emerald-600' : score >= 50 ? 'text-amber-500' : 'text-red-500';
  const scoreBg = score >= 75 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';

  const checkItems = [
    { key: 'schema_present', label: 'Schema Data', icon: <Code2 className="w-3.5 h-3.5" /> },
    { key: 'rich_metadata', label: 'Rich Metadata', icon: <Share2 className="w-3.5 h-3.5" /> },
    { key: 'structured_content', label: 'Structured Content', icon: <FileText className="w-3.5 h-3.5" /> },
    { key: 'author_attribution', label: 'Author Attribution', icon: <AtSign className="w-3.5 h-3.5" /> },
    { key: 'qa_structured', label: 'FAQ/HowTo Schema', icon: <Hash className="w-3.5 h-3.5" /> },
    { key: 'citation_links', label: 'Citation Links', icon: <Link2 className="w-3.5 h-3.5" /> },
  ];

  const checkStatus = (val) => {
    if (val === true) return 'pass';
    if (val === 'partial') return 'warn';
    return 'fail';
  };

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-600" />
          AI Search Readiness
          <Badge variant="outline" className="ml-auto">{level}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4">
          <div className={`text-3xl font-bold font-heading ${scoreColor}`}>{score}</div>
          <div className="flex-1">
            <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${scoreBg}`}
                initial={{ width: 0 }}
                animate={{ width: `${score}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Readiness for ChatGPT, Claude, Perplexity</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {checkItems.map(({ key, label, icon }) => {
            const status = checkStatus(checks[key]);
            return (
              <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${checkBadgeColor(status)}`}>
                <CheckDot status={status} />
                <span className="flex items-center gap-1">{icon} {label}</span>
              </div>
            );
          })}
        </div>

        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
          <p className="text-xs text-slate-500 mb-1">Content Depth</p>
          <p className={`text-sm font-semibold ${
            checks.content_depth === 'deep' ? 'text-emerald-600' :
            checks.content_depth === 'adequate' ? 'text-amber-500' : 'text-red-500'
          }`}>
            {checks.content_depth === 'deep' ? 'Deep — excellent for AI citation' :
             checks.content_depth === 'adequate' ? 'Adequate — could be more comprehensive' :
             'Thin — unlikely to be cited by AI engines'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 8. Usability Panel ─────────────────────────────────────────────────── */

const USABILITY_GRADE_COLORS = {
  'A+': { ring: '#10b981', text: 'text-emerald-600' },
  'A':  { ring: '#10b981', text: 'text-emerald-600' },
  'B':  { ring: '#3b82f6', text: 'text-blue-600' },
  'C':  { ring: '#f59e0b', text: 'text-amber-500' },
  'D':  { ring: '#f97316', text: 'text-orange-500' },
  'F':  { ring: '#ef4444', text: 'text-red-500' },
};

const GradeRing = ({ score, grade }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const filled = (score / 100) * circumference;
  const colors = USABILITY_GRADE_COLORS[grade] || USABILITY_GRADE_COLORS['F'];

  return (
    <div className="relative w-24 h-24 flex-shrink-0">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={radius} fill="none" stroke="currentColor"
          className="text-slate-200 dark:text-slate-700" strokeWidth="8" />
        <motion.circle
          cx="48" cy="48" r={radius} fill="none"
          stroke={colors.ring} strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - filled }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-2xl font-bold font-heading ${colors.text}`}>{grade}</span>
      </div>
    </div>
  );
};

const UsabilityCheckRow = ({ icon, label, pass, naLabel }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</p>
      {naLabel && (
        <p className="text-xs text-slate-500 mt-0.5">{naLabel}</p>
      )}
    </div>
    <div className="flex-shrink-0 mt-0.5">
      {pass === null ? (
        <span className="text-xs text-slate-400 italic">N/A</span>
      ) : pass ? (
        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
      ) : (
        <XCircle className="w-5 h-5 text-red-500" />
      )}
    </div>
  </div>
);

const DeviceFrame = ({ src, type }) => {
  const isPhone = type === 'mobile';
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`relative flex-shrink-0 ${isPhone ? 'w-28' : 'w-44'}`}>
        {isPhone ? (
          <div className="relative border-4 border-slate-800 dark:border-slate-600 rounded-[20px] overflow-hidden bg-white shadow-lg">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-3 bg-slate-800 dark:bg-slate-600 rounded-b-xl z-10" />
            <div className="pt-3 aspect-[9/16] overflow-hidden">
              {src ? (
                <img src={src} alt="Mobile rendering" className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                  <Smartphone className="w-8 h-8 text-slate-400" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative border-4 border-slate-800 dark:border-slate-600 rounded-lg overflow-hidden bg-white shadow-lg">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-2 bg-slate-800 dark:bg-slate-600 rounded-t-sm z-10" />
            <div className="pb-2 aspect-[4/3] overflow-hidden">
              {src ? (
                <img src={src} alt="Desktop rendering" className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                  <Monitor className="w-8 h-8 text-slate-400" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <span className="text-xs text-slate-500 capitalize">{type}</span>
    </div>
  );
};

export const UsabilityPanel = ({ usability }) => {
  if (!usability) return null;

  const { score, grade, headline, checks, performance_score, screenshot_mobile, screenshot_desktop } = usability;
  const colors = USABILITY_GRADE_COLORS[grade] || USABILITY_GRADE_COLORS['F'];

  const descriptionText =
    score >= 80
      ? 'Your page is well-optimised for usability across devices.'
      : 'Your page is OK but could be more usable across devices. Usability is important to maximise your available audience and minimise user bounce rates (which can indirectly affect your search engine rankings).';

  const hasViewport = checks?.has_viewport_meta;
  const cwvInsufficient = checks?.cwv_insufficient_data;
  const cwvPass = checks?.cwv_pass;
  const psiPass = performance_score != null ? performance_score >= 50 : null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Gauge className="w-5 h-5 text-sky-500" />
          Usability
          <Badge variant="outline" className="ml-auto text-xs">Mobile-First</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* ── Grade Ring + Headline ── */}
        <div className="flex items-center gap-4">
          <GradeRing score={score} grade={grade} />
          <div>
            <p className={`text-lg font-semibold font-heading ${colors.text}`}>{headline}</p>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{descriptionText}</p>
          </div>
        </div>

        {/* ── Device Rendering ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Device Rendering</p>
            <span className="text-xs text-slate-400 flex-1">
              This check visually demonstrates how your page renders on different devices.
            </span>
          </div>
          <div className="flex items-end gap-6 justify-center py-2">
            <DeviceFrame src={screenshot_mobile} type="mobile" />
            <DeviceFrame src={screenshot_desktop} type="desktop" />
          </div>
        </div>

        {/* ── Check Rows ── */}
        <div className="divide-y divide-slate-100 dark:divide-slate-700 rounded-xl border border-slate-100 dark:border-slate-700 px-3">
          <UsabilityCheckRow
            icon={<Activity className="w-4 h-4" />}
            label="Google's Core Web Vitals"
            pass={cwvInsufficient ? null : cwvPass}
            naLabel={
              cwvInsufficient
                ? "Google is indicating that they do not have 'sufficient real-world speed data for this page' in order to make a Core Web Vitals assessment."
                : undefined
            }
          />
          <UsabilityCheckRow
            label="Use of Mobile Viewports"
            pass={hasViewport}
            naLabel={
              hasViewport
                ? 'Your page specifies a Viewport matching the device\'s size, allowing it to render appropriately across devices.'
                : 'Your page does not specify a viewport meta tag. This may cause display issues on mobile devices.'
            }
          />
          <UsabilityCheckRow
            label="Google's PageSpeed Insights — Mobile"
            pass={psiPass}
            naLabel={
              psiPass === null
                ? 'PageSpeed data unavailable.'
                : psiPass
                ? `Performance score: ${performance_score} — your page performs adequately on mobile.`
                : `Performance score: ${performance_score ?? 'N/A'} — Google is indicating that your page is scoring poorly on their Mobile PageSpeed Insights evaluation.`
            }
          />
        </div>

      </CardContent>
    </Card>
  );
};

/* ─── 9. Performance & Resource Panel ────────────────────────────────────── */
const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ttfbStatus = (ms) => {
  if (ms == null) return { color: 'text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800', label: 'N/A' };
  if (ms <= 600) return { color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', label: 'Good' };
  if (ms <= 1500) return { color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', label: 'Needs Work' };
  return { color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20', label: 'Poor' };
};

export const PerformanceResourcePanel = ({ pageData }) => {
  if (!pageData) return null;
  const { ttfb_ms, page_size_bytes, is_compressed, asset_requests, dom_size, text_to_html_ratio } = pageData;
  const hasAny = ttfb_ms != null || page_size_bytes != null || dom_size != null;
  if (!hasAny) return null;

  const ttfb = ttfbStatus(ttfb_ms);
  const sizeStatus = page_size_bytes != null
    ? page_size_bytes < 500000 ? 'text-emerald-600' : page_size_bytes < 2000000 ? 'text-amber-500' : 'text-red-500'
    : 'text-slate-400';
  const domStatus = dom_size != null
    ? dom_size <= 1500 ? 'text-emerald-600' : dom_size <= 3000 ? 'text-amber-500' : 'text-red-500'
    : 'text-slate-400';
  const tthStatus = text_to_html_ratio != null
    ? text_to_html_ratio >= 10 ? 'text-emerald-600' : 'text-amber-500'
    : 'text-slate-400';

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Timer className="w-5 h-5 text-orange-500" />
          Performance & Resources
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className={`rounded-xl p-3 space-y-1 ${ttfb.bg}`}>
            <div className="flex items-center gap-1.5 text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              <span className="text-xs">TTFB</span>
            </div>
            <p className={`text-xl font-bold font-heading ${ttfb.color}`}>
              {ttfb_ms != null ? `${ttfb_ms}ms` : 'N/A'}
            </p>
            <p className="text-xs text-slate-500">{ttfb.label}</p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-slate-500">
              <HardDrive className="w-3.5 h-3.5" />
              <span className="text-xs">Page Size</span>
            </div>
            <p className={`text-xl font-bold font-heading ${sizeStatus}`}>
              {formatBytes(page_size_bytes)}
            </p>
            <p className="text-xs text-slate-500">{is_compressed ? 'Compressed' : 'Uncompressed'}</p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Network className="w-3.5 h-3.5" />
              <span className="text-xs">HTTP Requests</span>
            </div>
            <p className="text-xl font-bold font-heading">
              {asset_requests ?? 'N/A'}
            </p>
            <p className="text-xs text-slate-500">assets loaded</p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Layers className="w-3.5 h-3.5" />
              <span className="text-xs">DOM Size</span>
            </div>
            <p className={`text-xl font-bold font-heading ${domStatus}`}>
              {dom_size != null ? dom_size.toLocaleString() : 'N/A'}
            </p>
            <p className="text-xs text-slate-500">elements</p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-slate-500">
              <FileCode className="w-3.5 h-3.5" />
              <span className="text-xs">Text/HTML Ratio</span>
            </div>
            <p className={`text-xl font-bold font-heading ${tthStatus}`}>
              {text_to_html_ratio != null ? `${text_to_html_ratio}%` : 'N/A'}
            </p>
            <p className="text-xs text-slate-500">{text_to_html_ratio >= 10 ? 'Good' : 'Low'}</p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Wifi className="w-3.5 h-3.5" />
              <span className="text-xs">Compression</span>
            </div>
            <p className={`text-xl font-bold font-heading ${is_compressed ? 'text-emerald-600' : 'text-red-500'}`}>
              {is_compressed ? 'Enabled' : 'Disabled'}
            </p>
            <p className="text-xs text-slate-500">{is_compressed ? 'Gzip/Brotli' : 'Not detected'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 10. Technology Detection Panel ─────────────────────────────────────── */
export const TechnologyPanel = ({ technologies = [] }) => {
  if (!technologies || technologies.length === 0) return null;

  const techIcons = {
    'WordPress': '🔷',
    'Shopify': '🛒',
    'React': '⚛️',
    'Next.js': '▲',
    'Laravel': '🔻',
    'Django': '🐍',
    'Vue.js': '💚',
    'Angular': '🅰️',
  };

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Cpu className="w-5 h-5 text-violet-500" />
          Technologies Detected
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {technologies.map((tech, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 text-sm font-medium text-violet-700 dark:text-violet-300"
            >
              <span>{techIcons[tech] || <CircuitBoard className="w-3.5 h-3.5" />}</span>
              {tech}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 11. Social Links Panel ─────────────────────────────────────────────── */
const SOCIAL_ICONS = {
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  linkedin: Linkedin,
  instagram: Instagram,
  youtube: Youtube,
};

export const SocialLinksPanel = ({ socialLinks = [] }) => {
  if (!socialLinks || socialLinks.length === 0) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Share2 className="w-5 h-5 text-pink-500" />
          Social Media Links
          <Badge variant="outline" className="ml-auto text-xs">{socialLinks.length} found</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {socialLinks.map((link, i) => {
            const IconComp = SOCIAL_ICONS[link.network] || ExternalLink;
            return (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
              >
                <IconComp className="w-4 h-4 text-slate-500 group-hover:text-indigo-500 transition-colors" />
                <span className="text-sm text-slate-600 dark:text-slate-400 truncate flex-1">{link.url}</span>
                <span className="text-xs text-slate-400 capitalize">{link.network}</span>
              </a>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 12. Redirects & Mixed Content Panel ────────────────────────────────── */
export const RedirectsMixedContentPanel = ({ redirects = [], mixedContent = [] }) => {
  if ((!redirects || redirects.length === 0) && (!mixedContent || mixedContent.length === 0)) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-amber-500" />
          Redirects & Mixed Content
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {redirects.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Redirect Chain ({redirects.length} hop{redirects.length !== 1 ? 's' : ''})
            </p>
            <div className="space-y-1.5">
              {redirects.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">{r.status}</Badge>
                  <span className="text-slate-500 truncate">{r.from}</span>
                  <ArrowRightLeft className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300 truncate">{r.to}</span>
                </div>
              ))}
            </div>
            {redirects.length > 1 && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Multiple redirects slow down page load — reduce the chain to a single redirect.
              </p>
            )}
          </div>
        )}

        {mixedContent.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">
              Mixed Content ({mixedContent.length} resource{mixedContent.length !== 1 ? 's' : ''})
            </p>
            <div className="space-y-1.5">
              {mixedContent.slice(0, 10).map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <AlertOctagon className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">{item.tag}</Badge>
                  <span className="text-red-600 truncate">{item.url}</span>
                </div>
              ))}
              {mixedContent.length > 10 && (
                <p className="text-xs text-slate-500">+{mixedContent.length - 10} more resources</p>
              )}
            </div>
            <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
              <AlertOctagon className="w-3 h-3" />
              HTTP resources on an HTTPS page — browsers may block these and show security warnings.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── 13. Hreflang Panel ─────────────────────────────────────────────────── */
export const HreflangPanel = ({ hreflangTags = [] }) => {
  if (!hreflangTags || hreflangTags.length === 0) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Languages className="w-5 h-5 text-teal-500" />
          Hreflang Tags
          <Badge variant="outline" className="ml-auto text-xs">{hreflangTags.length} language{hreflangTags.length !== 1 ? 's' : ''}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {hreflangTags.map((tag, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <Badge variant="outline" className="text-xs font-mono">{tag.lang}</Badge>
              <span className="text-sm text-slate-600 dark:text-slate-400 truncate">{tag.href}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 14. Deprecated Tags & Emails Panel ─────────────────────────────────── */
export const CodeQualityPanel = ({ deprecatedTags = [], plaintextEmails = [] }) => {
  if ((!deprecatedTags || deprecatedTags.length === 0) && (!plaintextEmails || plaintextEmails.length === 0)) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <FileCode className="w-5 h-5 text-rose-500" />
          Code Quality Issues
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {deprecatedTags.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">Deprecated HTML Tags</p>
            <div className="flex flex-wrap gap-1.5">
              {deprecatedTags.map((tag, i) => (
                <span key={i} className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                  &lt;{tag}&gt;
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">Replace deprecated tags with modern CSS equivalents.</p>
          </div>
        )}

        {plaintextEmails.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">Plaintext Emails Exposed</p>
            <div className="space-y-1">
              {plaintextEmails.map((email, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-600 dark:text-slate-400">{email}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">Exposed emails attract spam bots. Use contact forms or obfuscation.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── 15. Render-Blocking Resources Panel ────────────────────────────────── */
export const RenderBlockingPanel = ({ renderBlocking = [] }) => {
  if (!renderBlocking || renderBlocking.length === 0) return null;

  const scripts = renderBlocking.filter(r => r.type === 'script');
  const styles = renderBlocking.filter(r => r.type === 'stylesheet');

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Blocks className="w-5 h-5 text-orange-500" />
          Render-Blocking Resources
          <Badge variant="outline" className="ml-auto text-xs">{renderBlocking.length} found</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          These resources block above-the-fold rendering. Consider deferring, async-loading, or inlining critical CSS.
        </p>
        {scripts.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Scripts ({scripts.length})</p>
            <div className="space-y-1">
              {scripts.slice(0, 8).map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">JS</Badge>
                  <span className="text-slate-600 dark:text-slate-400 truncate">{r.url}</span>
                </div>
              ))}
              {scripts.length > 8 && <p className="text-xs text-slate-400">+{scripts.length - 8} more</p>}
            </div>
          </div>
        )}
        {styles.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Stylesheets ({styles.length})</p>
            <div className="space-y-1">
              {styles.slice(0, 8).map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">CSS</Badge>
                  <span className="text-slate-600 dark:text-slate-400 truncate">{r.url}</span>
                </div>
              ))}
              {styles.length > 8 && <p className="text-xs text-slate-400">+{styles.length - 8} more</p>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── 16. Browser Caching & Minification Panel ───────────────────────────── */
export const CachingMinificationPanel = ({ caching, minification }) => {
  if (!caching && !minification) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Package className="w-5 h-5 text-cyan-500" />
          Caching & Minification
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {caching && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Browser Caching</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                caching.has_caching
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400'
                  : 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              }`}>
                {caching.has_caching ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                Cache-Control
              </div>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                caching.etag
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400'
                  : 'bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
              }`}>
                {caching.etag ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4 opacity-50" />}
                ETag
              </div>
              {caching.cache_control && (
                <div className="col-span-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-xs font-mono text-slate-600 dark:text-slate-400 break-all">
                  {caching.cache_control}
                </div>
              )}
            </div>
          </div>
        )}

        {minification && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Inline Minification</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
                <p className="text-xs text-slate-500">Inline JS</p>
                <p className={`text-lg font-bold font-heading ${minification.inline_js_unminified === 0 ? 'text-emerald-600' : 'text-amber-500'}`}>
                  {minification.inline_js_total - minification.inline_js_unminified}/{minification.inline_js_total}
                </p>
                <p className="text-xs text-slate-500">minified</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-1">
                <p className="text-xs text-slate-500">Inline CSS</p>
                <p className={`text-lg font-bold font-heading ${minification.inline_css_unminified === 0 ? 'text-emerald-600' : 'text-amber-500'}`}>
                  {minification.inline_css_total - minification.inline_css_unminified}/{minification.inline_css_total}
                </p>
                <p className="text-xs text-slate-500">minified</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── 17. Broken Links Panel ─────────────────────────────────────────────── */
export const BrokenLinksPanel = ({ brokenLinks = [] }) => {
  if (!brokenLinks || brokenLinks.length === 0) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <SearchX className="w-5 h-5 text-red-500" />
          Broken Links
          <Badge variant="outline" className="ml-auto text-xs bg-red-50 text-red-600 border-red-200">
            {brokenLinks.length} broken
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {brokenLinks.map((link, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700 border-red-200 flex-shrink-0">
                {link.status || 'ERR'}
              </Badge>
              <span className="text-sm text-red-700 dark:text-red-400 truncate flex-1">{link.url}</span>
              {link.text && <span className="text-xs text-red-500 truncate max-w-[120px]">"{link.text}"</span>}
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">Broken links hurt user experience and waste crawl budget. Fix or remove them.</p>
      </CardContent>
    </Card>
  );
};

/* ─── 18. SSL Certificate Panel ──────────────────────────────────────────── */
export const SSLCertPanel = ({ sslInfo }) => {
  if (!sslInfo || sslInfo.error) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-500" />
          SSL Certificate
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Issuer</p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{sslInfo.issuer}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Subject</p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">{sslInfo.subject}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Valid From</p>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{sslInfo.not_before}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-0.5">Expires</p>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{sslInfo.not_after}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 19. Analytics & CDN Panel ──────────────────────────────────────────── */
export const AnalyticsCDNPanel = ({ analyticsTools = [], cdn, httpVersion }) => {
  if ((!analyticsTools || analyticsTools.length === 0) && !cdn && !httpVersion) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-500" />
          Analytics & Infrastructure
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {analyticsTools.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Analytics Tracking</p>
            <div className="flex flex-wrap gap-2">
              {analyticsTools.map((tool, i) => (
                <div key={i} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm font-medium text-blue-700 dark:text-blue-300">
                  <RadioTower className="w-3.5 h-3.5" />
                  {tool}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {cdn && (
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
              <p className="text-xs text-slate-500 mb-0.5">CDN</p>
              <p className="text-sm font-semibold text-emerald-600">{cdn}</p>
            </div>
          )}
          {httpVersion && (
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
              <p className="text-xs text-slate-500 mb-0.5">Protocol</p>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{httpVersion}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 20. Font Optimization Panel ────────────────────────────────────────── */
export const FontOptimizationPanel = ({ fontInfo }) => {
  if (!fontInfo || fontInfo.total_fonts === 0) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <Paintbrush className="w-5 h-5 text-pink-500" />
          Font Optimization
          <Badge variant="outline" className={`ml-auto text-xs ${fontInfo.optimized ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {fontInfo.optimized ? 'Optimized' : 'Needs Work'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold font-heading">{fontInfo.total_fonts}</p>
            <p className="text-xs text-slate-500">Web Fonts</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
            <p className={`text-lg font-bold font-heading ${fontInfo.preloaded > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
              {fontInfo.preloaded}
            </p>
            <p className="text-xs text-slate-500">Preloaded</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
            <p className={`text-lg font-bold font-heading ${fontInfo.display_swap > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
              {fontInfo.display_swap}
            </p>
            <p className="text-xs text-slate-500">font-display:swap</p>
          </div>
        </div>
        {fontInfo.fonts?.length > 0 && (
          <div className="space-y-1">
            {fontInfo.fonts.slice(0, 5).map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {f.preloaded
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                <span className="text-slate-600 dark:text-slate-400 truncate">{f.url}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── 21. Iframes Panel ──────────────────────────────────────────────────── */
export const IframesPanel = ({ iframes = [] }) => {
  if (!iframes || iframes.length === 0) return null;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <BrainCircuit className="w-5 h-5 text-slate-500" />
          Embedded Iframes
          <Badge variant="outline" className="ml-auto text-xs">{iframes.length} found</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {iframes.map((iframe, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <Unplug className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-600 dark:text-slate-400 truncate flex-1">{iframe.src || '(empty src)'}</span>
              {iframe.title && <span className="text-xs text-slate-400 truncate max-w-[120px]">{iframe.title}</span>}
            </div>
          ))}
        </div>
        {iframes.length > 3 && (
          <p className="text-xs text-slate-500 mt-2">Multiple iframes can slow page load and affect SEO — review if all are necessary.</p>
        )}
      </CardContent>
    </Card>
  );
};
